// YNX — DIAGNOSTICS PANEL (Master Admin only).
// Live error feed + AI-generated weekly report (Claude Sonnet 4.6).
// Designed to be COPY-PASTED to Emergent Support in one click.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Stethoscope, RefreshCw, Sparkles, Copy, Trash2, AlertTriangle,
  Activity, Server, Globe, BadgeAlert, CheckCircle2,
} from 'lucide-react';
import {
  diagStats, diagListErrors, diagGenerateReport, diagLatestReport,
  diagClearErrors, diagEnsureIndexes,
} from '../lib/biz_diag';
import { MarkdownView } from '../components/MarkdownView';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch (_e) { return iso; }
};

export const DiagnosticsPanel = ({ secret }) => {
  const [stats, setStats]   = useState(null);
  const [errors, setErrors] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [filter, setFilter] = useState({ source: '', severity: '' });
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, list, last] = await Promise.all([
        diagStats(secret),
        diagListErrors(secret, { ...filter, limit: 150 }),
        diagLatestReport(secret),
      ]);
      setStats(s);
      setErrors(list.items || []);
      setReport(last);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [secret, filter]);

  useEffect(() => {
    diagEnsureIndexes(secret);
    refresh();
  }, [refresh, secret]);

  const generate = async () => {
    setGen(true);
    setError('');
    try {
      const r = await diagGenerateReport(secret);
      setReport(r);
    } catch (e) { setError(e.message); }
    finally { setGen(false); }
  };

  const copy = async () => {
    if (!report?.report_md) return;
    try {
      await navigator.clipboard.writeText(report.report_md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // Diagnostics reporter must NEVER recurse into itself or surface errors
      // to the host app. We log at debug level only so dev consoles see it.
      console.debug('YNX diagnostics: clipboard write failed', err);
    }
  };

  const purge = async () => {
    if (!window.confirm('Effacer DÉFINITIVEMENT toutes les erreurs ?')) return;
    try { await diagClearErrors(secret, 0); await refresh(); }
    catch (e) { alert(e.message); }
  };

  return (
    <section className='admin-section diag-section' data-testid='diag-panel'>
      <header className='diag-head'>
        <div>
          <h3><Stethoscope size={18} style={{ verticalAlign: '-3px' }} /> Auto-diagnostics IA</h3>
          <p className='diag-sub'>
            Détection automatique des bugs · Rapport hebdo IA pour Emergent Support
          </p>
        </div>
        <div className='diag-head-actions'>
          <button className='biz-btn-ghost sm' onClick={refresh} data-testid='diag-refresh-btn'>
            <RefreshCw size={12} /> Rafraîchir
          </button>
          <button className='biz-btn-primary sm' onClick={generate} disabled={gen} data-testid='diag-generate-btn'>
            <Sparkles size={12} /> {gen ? 'Génération IA…' : 'Générer rapport IA'}
          </button>
        </div>
      </header>

      {error && <div className='biz-error' data-testid='diag-error'>{error}</div>}

      {/* ─── STATS CARDS ─── */}
      {stats && (
        <div className='diag-stats'>
          <article className='diag-card'>
            <Activity size={16} />
            <div>
              <div className='diag-card-value'>{stats.totals?.all || 0}</div>
              <div className='diag-card-label'>Total erreurs</div>
            </div>
          </article>
          <article className='diag-card'>
            <BadgeAlert size={16} className='c-warn' />
            <div>
              <div className='diag-card-value'>{stats.totals?.last_24h || 0}</div>
              <div className='diag-card-label'>Dernières 24h</div>
            </div>
          </article>
          <article className='diag-card'>
            <BadgeAlert size={16} className='c-warn' />
            <div>
              <div className='diag-card-value'>{stats.totals?.last_7d || 0}</div>
              <div className='diag-card-label'>7 derniers jours</div>
            </div>
          </article>
          <article className='diag-card'>
            <Server size={16} className='c-info' />
            <div>
              <div className='diag-card-value'>{stats.totals?.backend || 0}</div>
              <div className='diag-card-label'>Backend</div>
            </div>
          </article>
          <article className='diag-card'>
            <Globe size={16} className='c-info' />
            <div>
              <div className='diag-card-value'>{stats.totals?.frontend || 0}</div>
              <div className='diag-card-label'>Frontend</div>
            </div>
          </article>
          <article className='diag-card'>
            <AlertTriangle size={16} className='c-danger' />
            <div>
              <div className='diag-card-value'>{stats.totals?.critical || 0}</div>
              <div className='diag-card-label'>Critiques</div>
            </div>
          </article>
        </div>
      )}

      {/* ─── AI WEEKLY REPORT ─── */}
      <div className='diag-report-block'>
        <header className='diag-report-head'>
          <h4>Rapport hebdomadaire IA</h4>
          <div className='diag-report-actions'>
            {report?.report_md && (
              <button className='biz-btn-ghost sm' onClick={copy} data-testid='diag-copy-btn'>
                <Copy size={12} /> {copied ? <><CheckCircle2 size={12} /> Copié pour Emergent !</> : 'Copier pour Emergent'}
              </button>
            )}
          </div>
        </header>
        {report?.report_md ? (
          <div className='diag-report-body'>
            <MarkdownView className='biz-markdown'>{report.report_md}</MarkdownView>
            <footer className='diag-report-foot'>
              <span>Généré le {fmt(report.created_at)}</span>
              {report.meta?.errors_count !== undefined && (
                <span>· {report.meta.errors_count} erreurs analysées</span>
              )}
              {report.meta?.ai_used === false && (
                <span className='c-warn'> · Mode dégradé (IA indisponible)</span>
              )}
            </footer>
          </div>
        ) : (
          <div className='biz-empty'>
            Aucun rapport généré pour le moment. Cliquez sur « Générer rapport IA »
            pour analyser les 7 derniers jours.
          </div>
        )}
      </div>

      {/* ─── TOP TYPES ─── */}
      {stats?.top_types_7d?.length > 0 && (
        <div className='diag-top-block'>
          <h4>Top types d'erreurs (7j)</h4>
          <ul className='diag-top-list'>
            {stats.top_types_7d.map((t) => (
              <li key={t.type}><b>{t.count}×</b> {t.type}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ─── ERROR LIST ─── */}
      <div className='diag-list-block'>
        <header className='diag-list-head'>
          <h4>Flux d'erreurs ({errors.length})</h4>
          <div className='diag-filters'>
            <select className='biz-input sm' value={filter.source}
              onChange={(e) => setFilter({ ...filter, source: e.target.value })}>
              <option value=''>Toutes sources</option>
              <option value='backend'>Backend</option>
              <option value='frontend'>Frontend</option>
            </select>
            <select className='biz-input sm' value={filter.severity}
              onChange={(e) => setFilter({ ...filter, severity: e.target.value })}>
              <option value=''>Toutes sévérités</option>
              <option value='critical'>Critique</option>
              <option value='error'>Erreur</option>
              <option value='warning'>Warning</option>
              <option value='info'>Info</option>
            </select>
            <button className='biz-btn-ghost sm danger' onClick={purge} title='Tout effacer'>
              <Trash2 size={12} /> Purger
            </button>
          </div>
        </header>

        {loading && <div className='biz-loading'>Chargement…</div>}
        {!loading && errors.length === 0 && (
          <div className='biz-empty'>Aucune erreur enregistrée. Tout va bien 🎉</div>
        )}

        <div className='diag-list'>
          {errors.map((e) => (
            <article key={e.id} className={`diag-row sev-${e.severity}`} onClick={() => setSelected(e)} data-testid={`diag-row-${e.id}`}>
              <span className={`diag-pill src-${e.source}`}>{e.source}</span>
              <span className='diag-type'>{e.error_type}</span>
              <span className='diag-msg'>{e.message}</span>
              <span className='diag-time'>{fmt(e.created_at)}</span>
            </article>
          ))}
        </div>
      </div>

      {/* ─── DETAIL MODAL ─── */}
      {selected && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setSelected(null)}>
          <div className='biz-modal'>
            <header className='biz-modal-head'>
              <h3>{selected.error_type}</h3>
              <button className='biz-icon-btn' onClick={() => setSelected(null)}>✕</button>
            </header>
            <div className='biz-modal-body diag-detail'>
              <div className='diag-detail-meta'>
                <span className={`diag-pill src-${selected.source}`}>{selected.source}</span>
                <span className={`diag-pill sev-${selected.severity}`}>{selected.severity}</span>
                <span>{fmt(selected.created_at)}</span>
              </div>
              <h4>Message</h4>
              <pre className='diag-pre'>{selected.message || '(vide)'}</pre>
              {selected.stack && (<>
                <h4>Stack trace</h4>
                <pre className='diag-pre'>{selected.stack}</pre>
              </>)}
              {selected.url && (<>
                <h4>URL</h4>
                <pre className='diag-pre'>{selected.url}</pre>
              </>)}
              {selected.user_agent && (<>
                <h4>User-Agent</h4>
                <pre className='diag-pre'>{selected.user_agent}</pre>
              </>)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DiagnosticsPanel;
